/**
 * Persistent user settings (localStorage; migrates to profile storage in
 * Phase 5). Corrupt or missing data falls back to defaults — loudly, via
 * console.warn, never silently swallowing a parse error's cause.
 */
export interface Settings {
  musicVolume: number;
  sfxVolume: number;
  audioOffsetMs: number;
  glow: boolean;
  particles: boolean;
  reducedFlashing: boolean;
  reducedMotion: boolean;
  /** Skip author-flagged low-detail objects (performance option). */
  lowDetail: boolean;
  /** UI scale multiplier (0.8×–1.5×). */
  uiScale: number;
  highContrast: boolean;
  /** Shape-code orbs/pads so kinds don't rely on color alone. */
  colorblind: boolean;
  /** Rebindable actions (KeyboardEvent.code values). */
  keyJump: string[];
  keyLeft: string[];
  keyRight: string[];
  /** Gamepad button indices (standard mapping) for the primary action. */
  padJump: number[];
  /** Frame cap: 0 = uncapped (vsync), otherwise 30/60. */
  fpsCap: number;
  /** Render resolution multiplier (0.5–1, on top of the DPR cap). */
  renderScale: number;
}

export const DEFAULT_SETTINGS: Settings = {
  musicVolume: 0.8,
  sfxVolume: 0.8,
  audioOffsetMs: 0,
  glow: true,
  particles: true,
  reducedFlashing: false,
  reducedMotion: false,
  lowDetail: false,
  uiScale: 1,
  highContrast: false,
  colorblind: false,
  keyJump: ['Space', 'ArrowUp', 'KeyW'],
  keyLeft: ['ArrowLeft', 'KeyA'],
  keyRight: ['ArrowRight', 'KeyD'],
  padJump: [0, 1, 2, 3],
  fpsCap: 0,
  renderScale: 1,
};

/** Quality presets map onto the individual toggles (which stay editable). */
export const QUALITY_PRESETS = {
  low: { glow: false, particles: false, lowDetail: true },
  medium: { glow: true, particles: false, lowDetail: false },
  high: { glow: true, particles: true, lowDetail: false },
} as const;

export type QualityPreset = keyof typeof QUALITY_PRESETS;

/** Heuristic auto-detect: weak/mobile hardware starts lower. */
export function detectQualityPreset(): QualityPreset {
  if (typeof navigator === 'undefined') return 'high';
  const cores = navigator.hardwareConcurrency ?? 8;
  const mobile = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);
  if (cores <= 2) return 'low';
  if (mobile || cores <= 4) return 'medium';
  return 'high';
}

import { mirrorKey } from './save-mirror';

const KEY = 'shape-shifters.settings.v1';

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      musicVolume: clamp01(parsed.musicVolume, DEFAULT_SETTINGS.musicVolume),
      sfxVolume: clamp01(parsed.sfxVolume, DEFAULT_SETTINGS.sfxVolume),
      audioOffsetMs: finiteOr(parsed.audioOffsetMs, 0, -500, 500),
      glow: parsed.glow ?? true,
      particles: parsed.particles ?? true,
      reducedFlashing: parsed.reducedFlashing ?? false,
      reducedMotion: parsed.reducedMotion ?? false,
      lowDetail: parsed.lowDetail ?? false,
      uiScale: finiteOr(parsed.uiScale, 1, 0.8, 1.5),
      highContrast: parsed.highContrast ?? false,
      colorblind: parsed.colorblind ?? false,
      keyJump: keyList(parsed.keyJump, DEFAULT_SETTINGS.keyJump),
      keyLeft: keyList(parsed.keyLeft, DEFAULT_SETTINGS.keyLeft),
      keyRight: keyList(parsed.keyRight, DEFAULT_SETTINGS.keyRight),
      padJump: buttonList(parsed.padJump, DEFAULT_SETTINGS.padJump),
      fpsCap: [0, 30, 60].includes(parsed.fpsCap as number) ? (parsed.fpsCap as number) : 0,
      renderScale: finiteOr(parsed.renderScale, 1, 0.5, 1),
    };
  } catch (e) {
    console.warn('Settings load failed; using defaults.', e);
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
    mirrorKey(KEY);
  } catch (e) {
    console.warn('Settings save failed (storage unavailable?).', e);
  }
}

function clamp01(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : fallback;
}

function finiteOr(v: unknown, fallback: number, min: number, max: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(min, Math.min(max, v)) : fallback;
}

function buttonList(v: unknown, fallback: number[]): number[] {
  if (!Array.isArray(v)) return [...fallback];
  const idx = v.filter((b): b is number => Number.isInteger(b) && b >= 0 && b <= 17);
  return idx.length > 0 ? idx.slice(0, 5) : [...fallback];
}

function keyList(v: unknown, fallback: string[]): string[] {
  if (!Array.isArray(v)) return [...fallback];
  const codes = v.filter(
    (c): c is string => typeof c === 'string' && c.length > 0 && c.length < 40,
  );
  return codes.length > 0 ? codes.slice(0, 5) : [...fallback];
}
